import { useState, useEffect, useCallback } from 'react';
import { useBilling } from '../../../context/BillingContext';
import { config } from '../../../config';

export interface Lesson {
    id: number;
    title: string;
    status: 'completed' | 'active' | 'locked';
    content?: string;
    unlockReason?: string;
}

export interface CoachDashboardData {
    sessions: any[];
    clients: any[];
    pendingVerifications: any[];
}

export interface BizBuildingState {
    lessons: Lesson[];
    currentLessonId: number | null;
    missions: any[];
    resources: any[];
    nextUnlockIn: number | null;
    programStartDate: string | null;
    isLoading: boolean;
    error: string | null;
}

export interface ForumThread {
    id: string;
    lessonId?: number;
    author: string;
    title: string;
    content: string;
    createdAt: string;
    repliesCount: number;
    lastActivity: string;
}

export interface ForumReply {
    id: string;
    author: string;
    content: string;
    createdAt: string;
}

export interface UseBizBuildingProgramReturn extends BizBuildingState {
    completeTask: (taskId: string) => Promise<void>;
    verifyMission: (missionId: string) => Promise<void>;
    refresh: () => Promise<void>;
    getCoachDashboard: (coachEmail: string) => Promise<CoachDashboardData>;
    bookSession: (userEmail: string, coachEmail: string, scheduledAt: string) => Promise<any>;
    getLessonResources: (lessonId: number | string) => Promise<any>;
    approveVerification: (sessionId: string) => Promise<any>;
    getAvailability: (coachEmail: string, date: string) => Promise<any>;
    cancelSession: (userEmail: string, sessionId: string) => Promise<any>;
    rescheduleSession: (userEmail: string, sessionId: string, newScheduledAt: string) => Promise<any>;
    getUserSessions: (userEmail: string) => Promise<any>;
    getForumThreads: () => Promise<ForumThread[]>;
    createForumThread: (title: string, content: string, lessonId?: number) => Promise<void>;
    getForumReplies: (threadId: string) => Promise<ForumReply[]>;
    postForumReply: (threadId: string, content: string) => Promise<void>;
    forumThreads: ForumThread[];
    forumThreadsLoading: boolean;
    coachDashboard: CoachDashboardData | null;
    coachDashboardLoading: boolean;
}

async function callBizBuilder(task: string, data?: any): Promise<any> {
    const response = await fetch(`${config.API_BASE_URL}/api/biz-builder/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ task, data }),
    });
    if (!response.ok) {
        const body = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(body.error || `BizBuilder API error: ${response.statusText}`);
    }
    return response.json();
}

export const useBizBuildingProgram = (): UseBizBuildingProgramReturn => {
    const { user } = useBilling();
    const [state, setState] = useState<BizBuildingState>({
        lessons: [],
        currentLessonId: null,
        missions: [],
        resources: [],
        nextUnlockIn: null,
        programStartDate: null,
        isLoading: true,
        error: null,
    });
    const [coachDashboard, setCoachDashboard] = useState<CoachDashboardData | null>(null);
    const [coachDashboardLoading, setCoachDashboardLoading] = useState(false);
    const [forumThreads, setForumThreads] = useState<ForumThread[]>([]);
    const [forumThreadsLoading, setForumThreadsLoading] = useState(false);

    const loadProgramData = useCallback(async () => {
        if (!user?.email) {return;}

        try {
            const response = await callBizBuilder('get_program_state', { userEmail: user.email });
            const data = response.result;

            if (!data || !Array.isArray(data.lessons)) {
                throw new Error('Failed to load program state from module');
            }

            setState({
                lessons: data.lessons,
                currentLessonId: data.currentLessonId ?? null,
                missions: data.missions || [],
                resources: data.resources || [],
                nextUnlockIn: data.nextUnlockIn ?? null,
                programStartDate: data.programStartDate ?? null,
                isLoading: false,
                error: null,
            });
        } catch (e: any) {
            setState(prev => ({ ...prev, isLoading: false, error: e.message }));
        }
    }, [user?.email]);

    useEffect(() => {
        loadProgramData();
    }, [loadProgramData]);

    const completeTask = async (taskId: string) => {
        if (!user?.email) {return;}

        try {
            await callBizBuilder('complete_task', { userEmail: user.email, taskId });
            await loadProgramData();
        } catch (e: any) {
            console.error('Failed to complete task:', e);
            throw e;
        }
    };

    const verifyMission = async (missionId: string) => {
        if (!user?.email) {return;}

        try {
            await callBizBuilder('verify_mission', { userEmail: user.email, missionId });
            await loadProgramData();
        } catch (e: any) {
            console.error('Failed to verify mission:', e);
            throw e;
        }
    };


    const getCoachDashboard = useCallback(async (coachEmail: string): Promise<CoachDashboardData> => {
        setCoachDashboardLoading(true);
        try {
            const response = await callBizBuilder('get_coach_dashboard', { coachEmail });
            const data = response.result || { sessions: [], clients: [], pendingVerifications: [] };
            setCoachDashboard(data);
            return data;
        } catch (e: any) {
            console.error('Failed to load coach dashboard:', e);
            throw e;
        } finally {
            setCoachDashboardLoading(false);
        }
    }, []);

    const bookSession = useCallback(async (userEmail: string, coachEmail: string, scheduledAt: string): Promise<any> => {
        try {
            const response = await callBizBuilder('book_session', { userEmail, coachEmail, scheduledAt });
            return response.result;
        } catch (e: any) {
            console.error('Failed to book session:', e);
            throw e;
        }
    }, []);

    const getLessonResources = useCallback(async (lessonId: number | string): Promise<any> => {
        try {
            const response = await callBizBuilder('get_lesson_resources', { lessonId });
            return response.result;
        } catch (e: any) {
            console.error('Failed to get lesson resources:', e);
            throw e;
        }
    }, []);

    const approveVerification = useCallback(async (sessionId: string): Promise<any> => {
        if (!user?.email) {return;}
        try {
            const response = await callBizBuilder('approve_verification', { userEmail: user.email, sessionId });
            if (coachDashboard && coachDashboard.sessions?.length > 0) {
                const coachEmail = coachDashboard.sessions[0].Coach_Email || user.email;
                getCoachDashboard(coachEmail).catch(console.error);
            }
            return response.result;
        } catch (e: any) {
            console.error('Failed to approve verification:', e);
            throw e;
        }
    }, [user?.email, coachDashboard, getCoachDashboard]);

    const getAvailability = useCallback(async (coachEmail: string, date: string): Promise<any> => {
        try {
            const response = await callBizBuilder('get_coach_availability', { coachEmail, date });
            return response.result;
        } catch (e: any) {
            console.error('Failed to get availability:', e);
            throw e;
        }
    }, []);

    const cancelSession = useCallback(async (userEmail: string, sessionId: string): Promise<any> => {
        try {
            const response = await callBizBuilder('cancel_session', { userEmail, sessionId });
            return response.result;
        } catch (e: any) {
            console.error('Failed to cancel session:', e);
            throw e;
        }
    }, []);

    const rescheduleSession = useCallback(async (userEmail: string, sessionId: string, scheduledAt: string): Promise<any> => {
        try {
            const response = await callBizBuilder('reschedule_session', { userEmail, sessionId, scheduledAt });
            return response.result;
        } catch (e: any) {
            console.error('Failed to reschedule session:', e);
            throw e;
        }
    }, []);

    const getForumThreads = useCallback(async (): Promise<ForumThread[]> => {
        if (!user?.email) {return [];}
        setForumThreadsLoading(true);
        try {
            const response = await callBizBuilder('get_forum_threads', { userEmail: user.email });
            const data = response.result || [];
            setForumThreads(data);
            return data;
        } catch (e: any) {
            console.error('Failed to load forum threads:', e);
            return [];
        } finally {
            setForumThreadsLoading(false);
        }
    }, [user?.email]);

    const createForumThread = useCallback(async (title: string, content: string, lessonId?: number): Promise<void> => {
        if (!user?.email) {return;}
        try {
            await callBizBuilder('create_forum_thread', { userEmail: user.email, title, content, lessonId });
            await getForumThreads();
        } catch (e: any) {
            console.error('Failed to create forum thread:', e);
            throw e;
        }
    }, [user?.email, getForumThreads]);

    const getForumReplies = useCallback(async (threadId: string): Promise<ForumReply[]> => {
        try {
            const response = await callBizBuilder('get_forum_replies', { threadId });
            return response.result || [];
        } catch (e: any) {
            console.error('Failed to load forum replies:', e);
            return [];
        }
    }, []);

    const postForumReply = useCallback(async (threadId: string, content: string): Promise<void> => {
        if (!user?.email) {return;}
        try {
            await callBizBuilder('post_forum_reply', { userEmail: user.email, threadId, content });
        } catch (e: any) {
            console.error('Failed to post reply:', e);
            throw e;
        }
    }, [user?.email]);

    const getUserSessions = useCallback(async (userEmail: string): Promise<any> => {
        try {
            const response = await callBizBuilder('get_user_sessions', { userEmail });
            return response.result;
        } catch (e: any) {
            console.error('Failed to get user sessions:', e);
            throw e;
        }
    }, []);

    return {
        ...state,
        completeTask,
        verifyMission,
        refresh: loadProgramData,
        getCoachDashboard,
        bookSession,
        getLessonResources,
        approveVerification,
        getAvailability,
        cancelSession,
        rescheduleSession,
        getUserSessions,
        coachDashboard,
        coachDashboardLoading,
        getForumThreads,
        createForumThread,
        getForumReplies,
        postForumReply,
        forumThreads,
        forumThreadsLoading,
    };
};
